import { InjectionToken } from '@angular/core';

export type TJkitTourEntry = string | readonly string[];
export type TJkitTourBreakpoint = 'mobile' | 'desktop';
export type TJkitTourStepPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';

export interface IJkitTourStep {
	id: string;
	title: string;
	description: string;
	isWelcome?: boolean;
	isFinish?: boolean;
	target?: string;
	placement?: TJkitTourStepPlacement;
	spotlightPadding?: number;
	scrollMargin?: number;
	welcomeImageSrc?: string;
	welcomeImageAlt?: string;
}

export interface IJkitTourConfig {
	id: string;
	entry: TJkitTourEntry;
	breakpoint?: TJkitTourBreakpoint;
	priority?: number;
	suppressReplay?: boolean;
	steps: readonly IJkitTourStep[];
}

export type TJkitTourUserState = 'Completed' | 'Pending';

export interface IJkitTourProgressRecord {
	TourId: string;
	UserId?: string;
	App?: string;
	State?: TJkitTourUserState;
	LastUpdated?: string;
}

export type TJkitTourStoreTo = 'storage' | 'api' | 'mixed';

export type TJkitTourPersistenceSaveReason = 'dismissed' | 'completed';

export interface IJkitTourPersistenceSavePayload {
	records: readonly IJkitTourProgressRecord[];
	record: IJkitTourProgressRecord;
	reason: TJkitTourPersistenceSaveReason;
	existed?: boolean;
}

export interface IJkitTourPersistenceAdapter {
	read?: () => readonly IJkitTourProgressRecord[] | Promise<readonly IJkitTourProgressRecord[] | null | undefined> | null | undefined;
	save: (payload: IJkitTourPersistenceSavePayload) => void | Promise<void>;
}

export interface IJkitStartTourOptions {
	force?: boolean;
}

export interface IJkitTourRuntimeConfig {
	tours?: readonly IJkitTourConfig[];
	texts?: Partial<IJkitTourTexts>;
	storageKey?: string;
	storeTo?: TJkitTourStoreTo;
	dismissible?: boolean;
	persistence?: IJkitTourPersistenceAdapter;
	resolveRecordMeta?: (() => Partial<IJkitTourProgressRecord> | null | undefined) | null;
	apiPendingEventName?: string;
	apiPendingGlobalKey?: string;
	apiSettledDelayMs?: number;
}

export interface IJkitTourResolvedRuntimeConfig {
	tours: readonly IJkitTourConfig[];
	texts: IJkitTourTexts;
	storageKey: string;
	storeTo: TJkitTourStoreTo;
	dismissible: boolean;
	persistence: IJkitTourPersistenceAdapter | null;
	resolveRecordMeta: (() => Partial<IJkitTourProgressRecord> | null | undefined) | null;
	apiPendingEventName: string;
	apiPendingGlobalKey: string;
	apiSettledDelayMs: number;
}

export interface IJkitTourTooltipSize {
	width: number;
	height: number;
}

export interface IJkitTourTooltipStyle {
	top: number;
	left: number;
	width: number;
	transform: 'none';
}

export interface IJkitTourSpotlightStyle {
	top: number;
	left: number;
	width: number;
	height: number;
}

export interface IJkitTourTexts {
	skipTour: string;
	previous: string;
	next: string;
	complete: string;
	getStarted: string;
	loading: string;
	hotkeysHint: string;
	stepBadgeIntro: string;
	stepBadgeComplete: string;
	stepBadgeTemplate: string;
}

export const JKIT_TOUR_STORAGE_KEY = 'jkitTours';
export const JKIT_TOUR_API_PENDING_EVENT = 'jkit:api-requests-pending-changed';
export const JKIT_TOUR_API_PENDING_GLOBAL_KEY = '__jkitPendingApiRequests';

export const JKIT_TOUR_RUNTIME_CONFIG = new InjectionToken<IJkitTourResolvedRuntimeConfig>('IJkitTourResolvedRuntimeConfig');

export const JkitTourDefaultRuntimeConfig: IJkitTourResolvedRuntimeConfig = {
	tours: [],
	texts: {
		skipTour: 'Skip Tour',
		previous: 'Previous',
		next: 'Next',
		complete: 'Complete',
		getStarted: 'Get Started',
		loading: 'Loading...',
		hotkeysHint: 'Esc dismiss, ← previous, → next, Enter next/complete.',
		stepBadgeIntro: 'Tour intro',
		stepBadgeComplete: "You're all set!",
		stepBadgeTemplate: 'Step {current} / {total}'
	},
	storageKey: JKIT_TOUR_STORAGE_KEY,
	storeTo: 'storage',
	dismissible: true,
	persistence: null,
	resolveRecordMeta: null,
	apiPendingEventName: JKIT_TOUR_API_PENDING_EVENT,
	apiPendingGlobalKey: JKIT_TOUR_API_PENDING_GLOBAL_KEY,
	apiSettledDelayMs: 420
};

function normalizeJkitTourText(value: string | undefined, fallback: string): string {
	if (typeof value !== 'string') return fallback;
	const normalized = value.trim();
	return normalized.length ? normalized : fallback;
}

function normalizeJkitTourPositiveMs(value: number | undefined, fallback: number): number {
	if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
	if (value < 0) return fallback;
	return Math.floor(value);
}

function normalizeJkitTourStoreTo(value: TJkitTourStoreTo | undefined): TJkitTourStoreTo {
	if (value === 'storage' || value === 'api' || value === 'mixed') return value;
	return JkitTourDefaultRuntimeConfig.storeTo;
}

function normalizeJkitTourTexts(texts: Partial<IJkitTourTexts> | undefined, fallback: IJkitTourTexts): IJkitTourTexts {
	return {
		skipTour: normalizeJkitTourText(texts?.skipTour, fallback.skipTour),
		previous: normalizeJkitTourText(texts?.previous, fallback.previous),
		next: normalizeJkitTourText(texts?.next, fallback.next),
		complete: normalizeJkitTourText(texts?.complete, fallback.complete),
		getStarted: normalizeJkitTourText(texts?.getStarted, fallback.getStarted),
		loading: normalizeJkitTourText(texts?.loading, fallback.loading),
		hotkeysHint: normalizeJkitTourText(texts?.hotkeysHint, fallback.hotkeysHint),
		stepBadgeIntro: normalizeJkitTourText(texts?.stepBadgeIntro, fallback.stepBadgeIntro),
		stepBadgeComplete: normalizeJkitTourText(texts?.stepBadgeComplete, fallback.stepBadgeComplete),
		stepBadgeTemplate: normalizeJkitTourText(texts?.stepBadgeTemplate, fallback.stepBadgeTemplate)
	};
}

export function resolveJkitTourRuntimeConfig(config: IJkitTourRuntimeConfig = {}): IJkitTourResolvedRuntimeConfig {
	const storeTo = normalizeJkitTourStoreTo(config.storeTo);
	const persistence = config.persistence ?? null;
	const resolveRecordMeta = config.resolveRecordMeta ?? null;
	if (storeTo === 'api' || storeTo === 'mixed') {
		if (!persistence?.save) {
			throw new Error('JkitTour: `persistence.save` is required when `storeTo` is `api` or `mixed`.');
		}

		if (!persistence.read) {
			throw new Error('JkitTour: `persistence.read` is required when `storeTo` is `api` or `mixed`.');
		}
	}

	return {
		tours: Array.isArray(config.tours) ? config.tours : JkitTourDefaultRuntimeConfig.tours,
		texts: normalizeJkitTourTexts(config.texts, JkitTourDefaultRuntimeConfig.texts),
		storageKey: normalizeJkitTourText(config.storageKey, JkitTourDefaultRuntimeConfig.storageKey),
		storeTo,
		dismissible: config.dismissible ?? JkitTourDefaultRuntimeConfig.dismissible,
		persistence,
		resolveRecordMeta,
		apiPendingEventName: normalizeJkitTourText(config.apiPendingEventName, JkitTourDefaultRuntimeConfig.apiPendingEventName),
		apiPendingGlobalKey: normalizeJkitTourText(config.apiPendingGlobalKey, JkitTourDefaultRuntimeConfig.apiPendingGlobalKey),
		apiSettledDelayMs: normalizeJkitTourPositiveMs(config.apiSettledDelayMs, JkitTourDefaultRuntimeConfig.apiSettledDelayMs)
	};
}
